import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Header.css";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EuroIcon from "@mui/icons-material/Euro";
import { authService } from "../../../features/auth/services/authService";
import { useCurrency } from "../../../contexts/CurrencyContext";

interface MenuItem {
  id: string;
  label: string;
  url?: string;
  parentId?: string | null;
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [mainMenuItems, setMainMenuItems] = useState<MenuItem[]>([]);
  const [isMenuPanelOpen, setMenuPanelOpen] = useState(false);
  const [openSubMenuId, setOpenSubMenuId] = useState<string | null>(null);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [currencyAnchor, setCurrencyAnchor] = useState<HTMLElement | null>(null);
  const { currency, setCurrency } = useCurrency();
  const burgerRef = useRef<HTMLButtonElement>(null);

  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => {
    if (authService.isAuthenticated()) {
      setAccountAnchor(event.currentTarget);
    } else {
      navigate("/auth");
    }
  };

  useEffect(() => {
    if (isMenuPanelOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setOpenSubMenuId(null);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuPanelOpen]);

  // Fetch menu items via sr-api backend
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/menu?slug=menu`)
      .then((res) => {
        setMainMenuItems(res.data || []);
      })
      .catch((err) => {
        console.error("Menu API error:", err);
      });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuPanelOpen(false);
        burgerRef.current?.focus();
      }
    };
    if (isMenuPanelOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuPanelOpen]);

  const handleOverlayClick = () => {
    setMenuPanelOpen(false);
    burgerRef.current?.focus();
  };

  const DISABLED_MENU_ITEMS = ['none'];

  const handleMenuItemClick = (item: MenuItem) => {
    // If this item has children, open submenu
    const hasChildren = mainMenuItems.some(
      (child) => child.parentId === item.id,
    );
    if (hasChildren) {
      setOpenSubMenuId(item.id);
    } else {
      setMenuPanelOpen(false);
      burgerRef.current?.focus();
    }
  };

  // Close menu when clicking outside the menu panel
  useEffect(() => {
    if (!isMenuPanelOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const menuPanel = document.querySelector(
        "#box-menu-custom > .elementor-shortcode",
      );
      if (menuPanel && !menuPanel.contains(event.target as Node)) {
        setMenuPanelOpen(false);
        burgerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuPanelOpen]);

  return (
    <AppBar
      position="fixed"
      color="transparent"
      sx={{
        height: "59px",
        background: "#1A1A1A4D",
        boxShadow: "none"
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 59, md: 59 },
          px: { xs: 1.5, md: 3 },
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <IconButton
          id="btn-menu"
          edge="start"
          color="inherit"
          aria-label="menu"
          aria-controls="main-menu-panel"
          aria-expanded={isMenuPanelOpen}
          onClick={() => setMenuPanelOpen(true)}
          ref={burgerRef}
          sx={{ zIndex: 1300, color: "#FFFFFF" }}
        >
          <MenuIcon fontSize="large" />
        </IconButton>
        <Box sx={{ position: "relative", width: { xs: "35%", sm: "25%", md: "20%" } }}>
          <a href={process.env.REACT_APP_HOME_URL}>
            <img 
                src={`${process.env.REACT_APP_HOME_URL}/wp-content/uploads/2025/07/20379043360d9c52d0769d1647730778994cc6ba.png`}
                alt='logo band'
                style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
                }}
            />
          </a>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 2 } }}>
          <IconButton color="inherit" onClick={handleAccountClick} sx={{ zIndex: 1300, color: "#FFFFFF" }}>
            <AccountCircleIcon />
          </IconButton>
          <Popover
            open={Boolean(accountAnchor)}
            anchorEl={accountAnchor}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 1 }}
          >
            <List sx={{ minWidth: 160, py: 0.5 }}>
              <ListItemButton onClick={() => { setAccountAnchor(null); navigate("/personal-info"); }}>
                <ListItemText primary="My Account" />
              </ListItemButton>
              <ListItemButton onClick={() => { setAccountAnchor(null); navigate("/user-setting"); }}>
                <ListItemText primary="Setting" />
              </ListItemButton>
              <ListItemButton onClick={() => { setAccountAnchor(null); authService.logout(); navigate("/auth"); }}>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </List>
          </Popover>
          <IconButton color="inherit" onClick={() => navigate("/cart")} sx={{ zIndex: 1300, color: "#FFFFFF" }}>
            <ShoppingBagIcon />
          </IconButton>
          <IconButton color="inherit" onClick={(e) => setCurrencyAnchor(e.currentTarget)} sx={{ zIndex: 1300, color: "#FFFFFF" }}>
            <CurrencyExchangeIcon />
          </IconButton>
          <Popover
            open={Boolean(currencyAnchor)}
            anchorEl={currencyAnchor}
            onClose={() => setCurrencyAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 1 }}
          >
            <List sx={{ minWidth: 160, py: 0.5 }}>
              <ListItemButton
                selected={currency === 'THB'}
                onClick={() => { setCurrency('THB'); setCurrencyAnchor(null); }}
              >
                <ListItemText primary="(฿) Baht (TH)" />
              </ListItemButton>
              <ListItemButton
                selected={currency === 'EUR'}
                onClick={() => { setCurrency('EUR'); setCurrencyAnchor(null); }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EuroIcon fontSize="small" />
                  <ListItemText primary="Euro" />
                </Box>
              </ListItemButton>
            </List>
          </Popover>
        </Box>
      </Toolbar>
      {/* Custom Overlay Menu */}
      <div id="box-menu-custom" className={isMenuPanelOpen ? "show" : ""}>
        <div className="elementor-shortcode">
          <div id="btn-close" onClick={handleOverlayClick}>
            ✕
          </div>
          <ul className="main-menu-list">
            {mainMenuItems
              .filter((item) => !item.parentId)
              .filter((item) => !DISABLED_MENU_ITEMS.includes(item.label))
              .filter((item) => !openSubMenuId || openSubMenuId === item.id)
              .map((item) => {
                const hasChildren = mainMenuItems.some(
                  (child) => child.parentId === item.id,
                );
                const isActive = openSubMenuId === item.id;
                return (
                  <li
                    className={"main-menu-item" + (isActive ? " active" : "")}
                    id={item.label.replace(/\s+/g, "_").toLowerCase()}
                    key={item.id}
                    style={isActive ? { borderRadius: "12px" } : {}}
                  >
                    <p onClick={() => handleMenuItemClick(item)}>
                      <a
                        href={item.url}
                        style={{ color: "unset", textDecoration: "unset" }}
                      >
                        {item.label}
                      </a>
                    </p>
                    {hasChildren && (
                      <span
                        className={
                          "submenu-toggle" + (isActive ? " active" : "")
                        }
                        onClick={() =>
                          setOpenSubMenuId(isActive ? null : item.id)
                        }
                      >
                        <ChevronRightIcon />
                      </span>
                    )}
                  </li>
                );
              })}
          </ul>
          {/* Submenu overlays next to main menu, styled like main menu */}
          {openSubMenuId && (
            <ul className="submenu show" style={{ display: "block" }}>
              {mainMenuItems
                .filter((item) => item.parentId === openSubMenuId)
                .map((sub) => {
                  return (
                    <li
                      className="main-menu-item"
                      key={sub.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        margin: "10px 0",
                      }}
                    >
                      <p
                        onClick={() => handleMenuItemClick(sub)}
                        style={{
                          fontWeight: "400",
                          color: "#4D4D4D",
                          margin: 0,
                        }}
                      >
                        <a
                          href={sub.url}
                          style={{ color: "unset", textDecoration: "unset" }}
                        >
                          {sub.label}
                        </a>
                      </p>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </div>
    </AppBar>
  );
};

export default Header;
